import {
  getReadableShiftCodeTextColor,
  getSettingShiftCodeDisplayText,
  getSettingShiftCodeFontSize,
} from "@utils/fontSizeUtils";
import type * as type from "@vTypes";
import { PencilLine, Trash2 } from "lucide-react";
import type { ReactNode } from "react";
import { useMemo, useState } from "react";
import LoadingSpiner from "@/components/ui/LoadingSpiner";
import {
  ShiftCodeFormDialog,
  type ShiftCodeFormSubmitValue,
} from "./ShiftCodeFormDialog";
import {
  canUseOffSwapTarget,
  isSameShiftManage,
  nextEditedShiftManage,
} from "./shift-code-add-dialog-utils";
import { StatusBadge, TimeRangeCell } from "./conf-table-row-display";

function getSlotIndex(
  data: type.UpdateShift,
  currentShiftManage: type.ShiftManageObject[]
) {
  if (data.shift_gb === "고정") return 4;
  if (data.type !== "근무") return 0;
  if (data.shift_gb === "미드" || data.default_shift === "M") return 0;

  const managedSlot = currentShiftManage.find((item) =>
    item.codes.includes(data.shift_id)
  )?.shift_slot;
  if (managedSlot != null) return managedSlot;

  if (data.shift_gb === "데이" || data.default_shift === "D") return 1;
  if (data.shift_gb === "이브닝" || data.default_shift === "E") return 2;
  if (data.shift_gb === "나이트" || data.default_shift === "N") return 3;

  return 0;
}

function getSlotText(
  data: type.UpdateShift,
  currentShiftManage: type.ShiftManageObject[]
) {
  if (data.default_shift === "주" || data.default_shift === "O") {
    return "오프";
  }
  if (data.shift_gb === "고정") return "고정근무";
  if (data.type !== "근무") return "-";
  if (data.shift_gb === "미드" || data.default_shift === "M") return "미드";

  const slotIndex = getSlotIndex(data, currentShiftManage);
  if (slotIndex === 1) return "데이";
  if (slotIndex === 2) return "이브닝";
  if (slotIndex === 3) return "나이트";
  return data.shift_gb || "-";
}

function getHighlightedText(text: string, searchTerm: string): ReactNode {
  const normalizedSearchTerm = searchTerm.trim().toLowerCase();
  if (!normalizedSearchTerm) return text;

  const lowerText = text.toLowerCase();
  const fragments: ReactNode[] = [];
  let cursor = 0;
  let matchStart = lowerText.indexOf(normalizedSearchTerm);

  while (matchStart >= 0) {
    const matchEnd = matchStart + normalizedSearchTerm.length;

    if (matchStart > cursor) {
      fragments.push(text.slice(cursor, matchStart));
    }
    fragments.push(
      <mark
        key={`${matchStart}-${matchEnd}`}
        className="rounded-[3px] bg-[#FFF3A3] px-0.5 text-inherit"
      >
        {text.slice(matchStart, matchEnd)}
      </mark>
    );

    cursor = matchEnd;
    matchStart = lowerText.indexOf(normalizedSearchTerm, cursor);
  }

  if (fragments.length === 0) return text;
  if (cursor < text.length) {
    fragments.push(text.slice(cursor));
  }

  return <>{fragments}</>;
}

function ConfTableRow({
  rowNumber,
  data,
  updateShift,
  deleteShift,
  currentShiftManage,
  saveShiftManage,
  shiftIdFlatMap,
  offSwapOffShiftGb,
  onlyTimeAndColorEditable = false,
  isPending = false,
  searchTerm = "",
  searchField = "name",
}: {
  readonly rowNumber?: number;
  readonly data: type.UpdateShift;
  readonly updateShift: (value: type.UpdateShift) => void;
  readonly deleteShift: (value: string) => void;
  readonly currentShiftManage: type.ShiftManageObject[];
  readonly saveShiftManage: (value: type.ShiftManageObject[]) => void;
  readonly shiftIdFlatMap: readonly string[];
  readonly offSwapOffShiftGb?: string | null;
  readonly onlyTimeAndColorEditable?: boolean;
  readonly isPending?: boolean;
  readonly searchTerm?: string;
  readonly searchField?: "name" | "memo";
}) {
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const isDefaultShift = Boolean(data.default_shift);
  const displayShiftId = getSettingShiftCodeDisplayText(data.shift_id);
  const displayTextColor = getReadableShiftCodeTextColor(data.color);
  const slotIndex = useMemo(
    () => getSlotIndex(data, currentShiftManage),
    [currentShiftManage, data]
  );
  const slotText = useMemo(
    () => getSlotText(data, currentShiftManage),
    [currentShiftManage, data]
  );
  const readonlySlotLabel =
    isDefaultShift && (data.default_shift === "M" || data.shift_gb === "미드")
      ? "미드"
      : null;
  const offSwapVisible = canUseOffSwapTarget(data, offSwapOffShiftGb);

  const handleSubmitEdit = (value: ShiftCodeFormSubmitValue) => {
    const nextShift: type.UpdateShift = {
      ...data,
      shift_id: isDefaultShift ? data.shift_id : value.shift_id,
      name: value.name,
      description: value.description,
      color: value.color,
      start_time: value.start_time,
      end_time: value.end_time,
      type: isDefaultShift ? data.type : value.type,
      shift_gb: isDefaultShift ? data.shift_gb : value.shift_gb,
      default_shift: data.default_shift,
      id: data.id,
      allday: data.allday,
      auto_schedule: data.auto_schedule,
      duration: data.duration,
      show_in_preference: value.show_in_preference,
      off_swap_target: value.off_swap_target,
      health_leave_target: value.health_leave_target,
      sleep_off_target: value.sleep_off_target,
    };
    const nextShiftManage = isDefaultShift
      ? currentShiftManage
      : nextEditedShiftManage(
          currentShiftManage,
          data.shift_id,
          value.shift_id,
          value.slotIndex
        );

    updateShift(nextShift);
    if (!isSameShiftManage(currentShiftManage, nextShiftManage)) {
      saveShiftManage(nextShiftManage);
    }
    setIsEditDialogOpen(false);
  };

  const handleDelete = () => {
    const nextShiftManage = currentShiftManage.map((item) => ({
      ...item,
      codes: item.codes.filter((code) => code !== data.shift_id),
    }));

    deleteShift(data.shift_id);
    if (!isSameShiftManage(currentShiftManage, nextShiftManage)) {
      saveShiftManage(nextShiftManage);
    }
  };

  return (
    <div className="conf_shiftCode__gridTemplate conf_shiftCode__row hover:bg-zinc-200/50 relative">
      {isPending ? (
        <div
          role="status"
          aria-label="Saving"
          className="absolute inset-0 z-[100] flex items-center justify-center bg-white/40"
        >
          <LoadingSpiner size="sm" className="w-auto" />
        </div>
      ) : null}

      <div className="conf_shiftCode__td">
        <span className="font-pretendard text-sm font-semibold text-[#666666]">
          {rowNumber ?? "-"}
        </span>
      </div>

      <div className="conf_shiftCode__td">
        <div
          className="conf_shiftDisplay"
          style={{ backgroundColor: data.color, color: displayTextColor }}
          title={data.shift_id}
        >
          <span
            className="font-pretendard font-bold whitespace-nowrap"
            style={{
              fontSize: getSettingShiftCodeFontSize(displayShiftId),
            }}
          >
            {displayShiftId}
          </span>
        </div>
      </div>

      <div className="conf_shiftCode__td">
        <div className="conf_shiftName">
          {getHighlightedText(data.name, searchField === "name" ? searchTerm : "")}
        </div>
      </div>

      <div className="conf_shiftCode__td">
        <div className="conf_selectType">{slotText}</div>
      </div>

      <TimeRangeCell
        type={data.type}
        startTime={data.start_time}
        endTime={data.end_time}
      />

      <div className="conf_shiftCode__td">
        <div className="conf_selectType">{data.type}</div>
      </div>

      <div className="conf_shiftCode__td">
        <StatusBadge
          checked={Boolean(data.off_swap_target)}
          label={`${data.shift_id} OFF 대체 코드 지정 상태`}
          activeText="대체"
          inactiveText="미대체"
          unavailable={!offSwapVisible}
        />
      </div>

      <div className="conf_shiftCode__td">
        <StatusBadge
          checked={Boolean(data.show_in_preference)}
          label={`${data.shift_id} 원티드 반영 상태`}
        />
      </div>

      <div className="conf_shiftCode__td">
        <div className="conf_shiftDescription" title={data.description ?? ""}>
          {data.description
            ? getHighlightedText(
                data.description,
                searchField === "memo" ? searchTerm : ""
              )
            : "-"}
        </div>
      </div>

      <div className="conf_shiftCode__td">
        <div className="flex w-full items-center justify-center gap-1">
          <button
            type="button"
            className="rounded p-1 transition-colors hover:bg-blue-100 disabled:cursor-not-allowed"
            disabled={isPending}
            aria-label={`${data.shift_id} 수정`}
            onClick={() => setIsEditDialogOpen(true)}
          >
            <PencilLine className="size-4 text-[#D6D6DE] transition-colors hover:text-[#0E59C6]" />
          </button>
          {isDefaultShift ? (
            <span className="size-6" aria-hidden="true" />
          ) : (
            <button
              type="button"
              className="rounded p-1 transition-colors hover:bg-blue-100 disabled:cursor-not-allowed"
              disabled={isPending}
              title="삭제"
              aria-label={`${data.shift_id} 삭제`}
              onClick={handleDelete}
            >
              <Trash2 className="size-4 text-[#D6D6DE] transition-colors hover:text-[#0E59C6]" />
            </button>
          )}
        </div>
      </div>

      {isEditDialogOpen ? (
        <ShiftCodeFormDialog
          open={isEditDialogOpen}
          mode="edit"
          title="근무코드 수정"
          description="코드 정보와 타입별 조건을 같은 흐름에서 수정합니다."
          initialValue={data}
          initialSlotIndex={slotIndex}
          duplicateShiftIds={shiftIdFlatMap}
          identityDisabled={onlyTimeAndColorEditable}
          shiftCodeDisabled={isDefaultShift || onlyTimeAndColorEditable}
          workStructureDisabled={isDefaultShift || onlyTimeAndColorEditable}
          readonlySlotLabel={readonlySlotLabel}
          offSwapEditable={offSwapVisible}
          offSwapOffShiftGb={offSwapOffShiftGb}
          isPending={isPending}
          onOpenChange={(open) => {
            if (!isPending) {
              setIsEditDialogOpen(open);
            }
          }}
          onSubmit={handleSubmitEdit}
        />
      ) : null}
    </div>
  );
}

export default ConfTableRow;
